import { Provider } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { EMPTY, Observable, shareReplay, switchMap } from 'rxjs';

import { UserRoles } from '@core/consts/userRoles';
import { InitializationResult, UserInitialization } from '@core/models/user-initialization.interface';
import { UserStateService } from '@core/services/authApiService/user-state-service.service';
import { USER_INITIALIZATION } from '@core/tokens/user-initialization.token';
import { ClinicianInitializationService } from '@pages/public/clinician-portal/services/clinician-initialization.service';
import { FacilityInitializationService } from '@pages/public/facility-portal/services/facility-initialization.service';
import { PatientInitializationService } from '@pages/public/patient-portal/services/patient-initialization.service';
import { SysadminInitializationService } from '@pages/public/sysadmin-panel/services/sysadmin-initialization.service';

export function initUserFactory(
  userState: UserStateService,
  patientInit: PatientInitializationService,
  headOfPracticeInit: FacilityInitializationService,
  clinicianInit: ClinicianInitializationService,
  sysadminInit: SysadminInitializationService,
): UserInitialization {
  const sharedInitialization$ = toObservable(userState.role).pipe(
    switchMap((role) => {
      switch (role) {
        case UserRoles.PATIENT:
          return patientInit.initialize();
        case UserRoles.HEAD_OF_PRACTICE:
          return headOfPracticeInit.initialize();
        case UserRoles.CLINICIAN:
          return clinicianInit.initialize();
        case UserRoles.SYSADMIN:
          return sysadminInit.initialize();
        default:
          return EMPTY;
      }
    }),
    shareReplay(1),
  );

  return {
    initialize(): Observable<InitializationResult> {
      return sharedInitialization$;
    },
  };
}

export const userInitializationProvider: Provider = {
  provide: USER_INITIALIZATION,
  useFactory: initUserFactory,
  deps: [
    UserStateService,
    PatientInitializationService,
    FacilityInitializationService,
    ClinicianInitializationService,
    SysadminInitializationService,
  ],
};
